import { useEffect, useMemo, useState } from 'react';
import { api } from '../api';
import { ApiError, type GmAtributos, type GmSkill, type GmSkills, type SkillLock } from '../api/types';
import { idiomaAtual, t } from '../i18n/idioma';
import { nomeDaSkill } from '../i18n/nomes';

interface Props {
  characterId: string;
  name: string;
  onDirty(dirty: boolean): void;
  onAplicou(): void;
  onClose(): void;
}

/* Furtividade saiu do jogo por enquanto (SkillIds.ForaDoJogo no servidor): sem a aba. */
const GROUP_ORDER = ['combate', 'magia', 'profissao'] as const;
const GROUP_CHAVE: Record<(typeof GROUP_ORDER)[number], string> = {
  combate: 'perfil.grupoCombate',
  magia: 'perfil.grupoMagia',
  profissao: 'perfil.grupoProfissao'
};

const LOCK_ICON: Record<SkillLock, string> = {
  raise: '▲',
  locked: '■',
  lower: '▼'
};

const GABARITOS = {
  warrior: { axing: 50, tactics: 30, healing: 20 },
  mage: { magery: 50, meditation: 30, magicResist: 20 },
  smith: { blacksmithing: 50, mining: 30, maceFighting: 20 }
} as const;

type Atalho = 'zero' | keyof typeof GABARITOS;

/** Os três atributos base, na ordem do perfil (techspec-gm-atributos). */
const ATRIBUTOS = [
  ['for', 'gm.atributos.for'],
  ['des', 'gm.atributos.des'],
  ['int', 'gm.atributos.int']
] as const;
type AtribId = (typeof ATRIBUTOS)[number][0];

function rascunhoDosAtributos(a: GmAtributos | undefined): Record<AtribId, string> {
  return { for: String(a?.for ?? ''), des: String(a?.des ?? ''), int: String(a?.int ?? '') };
}

/** Inteiro de 1 ao teto (100), ou null. */
function lerAtributo(raw: string, teto: number): number | null {
  const n = Number(raw.trim());
  return Number.isInteger(n) && n >= 1 && n <= teto ? n : null;
}

function oneDecimal(n: number): string {
  return n.toLocaleString(idiomaAtual(), { minimumFractionDigits: 1, maximumFractionDigits: 1 });
}

function parseCasa(raw: string): number | null {
  const n = Number(raw.trim().replace(',', '.'));
  if (!Number.isFinite(n)) return null;
  return n;
}

function casaOk(n: number): boolean {
  if (n < 0 || n > 100) return false;
  return Math.abs(Math.round(n * 10) / 10 - n) < 1e-9;
}

function sinal(n: number): string {
  const txt = oneDecimal(Math.abs(n));
  if (n > 0) return `+${txt}`;
  if (n < 0) return `−${txt}`;
  return oneDecimal(0);
}

function valoresDoAtalho(ids: readonly string[], atalho: Atalho): Record<string, number> {
  if (atalho === 'zero') return Object.fromEntries(ids.map(id => [id, 0]));
  const gab = GABARITOS[atalho];
  return Object.fromEntries(ids.map(id => [id, gab[id as keyof typeof gab] ?? 0]));
}

export function GmSkillsGump({ characterId, name, onDirty, onAplicou, onClose }: Props) {
  const [sheet, setSheet] = useState<GmSkills | null>(null);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [atalhos, setAtalhos] = useState(false);
  const [atalho, setAtalho] = useState<Atalho | null>(null);
  const [aplicado, setAplicado] = useState<{ n: number; delta: number; atrib: number } | null>(null);
  const [atribDraft, setAtribDraft] = useState<Record<AtribId, string>>(rascunhoDosAtributos(undefined));

  useEffect(() => {
    let live = true;
    setSheet(null);
    setAplicado(null);
    setAtalho(null);
    setError(null);
    api.gmSkills(characterId)
      .then(next => {
        if (!live) return;
        setSheet(next);
        setDraft(Object.fromEntries(next.skills.map(s => [s.id, oneDecimal(s.value)])));
        setAtribDraft(rascunhoDosAtributos(next.atributos));
      })
      .catch(err => {
        if (live) setError(err instanceof ApiError ? err.message : t('gm.skills.naoLeu'));
      });
    return () => { live = false; };
  }, [characterId]);

  const parsed = useMemo(() => {
    if (!sheet) return null;
    const valores: Record<string, number> = {};
    let ok = true;
    for (const skill of sheet.skills) {
      const n = parseCasa(draft[skill.id] ?? '');
      if (n === null || !casaOk(n)) ok = false;
      else valores[skill.id] = Math.round(n * 10) / 10;
    }
    return { valores, ok };
  }, [sheet, draft]);

  const total = parsed && parsed.ok
    ? sheet!.skills.reduce((n, s) => n + parsed.valores[s.id], 0)
    : sheet?.total ?? 0;
  const cap = sheet?.skillCap ?? 500;
  const estoura = total > cap;
  const sobra = cap - total;
  const mexidas = sheet
    ? sheet.skills.filter(s => parsed?.ok && parsed.valores[s.id] !== s.value)
    : [];
  // Atributos (techspec-gm-atributos): inteiros de 1 ao teto, a soma no StatCap. Servidor antigo não manda: sem a seção.
  const atrib = sheet?.atributos;
  const atribValores = atrib
    ? Object.fromEntries(ATRIBUTOS.map(([id]) => [id, lerAtributo(atribDraft[id], atrib.teto)])) as Record<AtribId, number | null>
    : null;
  const atribOk = !atribValores || ATRIBUTOS.every(([id]) => atribValores[id] !== null);
  const atribSoma = atribValores && atribOk ? ATRIBUTOS.reduce((n, [id]) => n + (atribValores[id] ?? 0), 0) : 0;
  const atribEstoura = !!atrib && atribOk && atribSoma > atrib.statCap;
  const atribMexidos = atrib && atribValores && atribOk ? ATRIBUTOS.filter(([id]) => atribValores[id] !== atrib[id]) : [];
  const dirty = mexidas.length > 0 || atribMexidos.length > 0;
  const deltaTotal = mexidas.reduce((n, s) => n + ((parsed?.valores[s.id] ?? s.value) - s.value), 0);
  const travadas = mexidas.filter(s => s.lock === 'locked');

  useEffect(() => { onDirty(dirty); }, [dirty, onDirty]);

  const grupos = useMemo(() => {
    if (!sheet) return [];
    const map = new Map<string, GmSkill[]>();
    for (const skill of sheet.skills) {
      const list = map.get(skill.group) ?? [];
      list.push(skill);
      map.set(skill.group, list);
    }
    return GROUP_ORDER
      .map(id => ({ id, skills: map.get(id) ?? [] }))
      .filter(g => g.skills.length > 0);
  }, [sheet]);

  const aplicarAtalho = (id: Atalho) => {
    if (!sheet) return;
    const next = valoresDoAtalho(sheet.skills.map(s => s.id), id);
    setDraft(Object.fromEntries(sheet.skills.map(s => [s.id, oneDecimal(next[s.id] ?? 0)])));
    setAtalho(id);
    setAplicado(null);
  };

  const reverter = () => {
    if (!sheet) return;
    setDraft(Object.fromEntries(sheet.skills.map(s => [s.id, oneDecimal(s.value)])));
    setAtribDraft(rascunhoDosAtributos(sheet.atributos));
    setAtalho(null);
    setAplicado(null);
  };

  const aplicar = async () => {
    if (!sheet || !parsed?.ok || estoura || !atribOk || atribEstoura || !dirty) return;
    setBusy(true);
    setError(null);
    try {
      const next = await api.gmSetSkills(
        characterId,
        sheet.skills.map(s => ({ id: s.id, value: parsed.valores[s.id] })),
        undefined,
        atribValores && atribMexidos.length > 0
          ? { for: atribValores.for!, des: atribValores.des!, int: atribValores.int! }
          : undefined
      );
      setSheet(next);
      setDraft(Object.fromEntries(next.skills.map(s => [s.id, oneDecimal(s.value)])));
      setAtribDraft(rascunhoDosAtributos(next.atributos));
      setAtalho(null);
      setAplicado({ n: mexidas.length, delta: deltaTotal, atrib: atribMexidos.length });
      onAplicou();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('gm.skills.naoAplicou'));
    } finally {
      setBusy(false);
    }
  };

  const contaGabarito = () => {
    if (!sheet || (atalho !== 'warrior' && atalho !== 'mage' && atalho !== 'smith')) return null;
    const gab = GABARITOS[atalho];
    let zerarN = 0, zerarV = 0, rebaixarN = 0, rebaixarV = 0;
    for (const s of sheet.skills) {
      const alvo = gab[s.id as keyof typeof gab] ?? 0;
      const d = alvo - s.value;
      if (s.id in gab) {
        if (d < 0) { rebaixarN++; rebaixarV += d; }
      } else if (s.value > 0) {
        zerarN++;
        zerarV += d;
      }
    }
    return { zerarN, zerarV, rebaixarN, rebaixarV, total: zerarV + rebaixarV };
  };

  const gabarito = contaGabarito();
  const barPct = estoura ? 100 : cap <= 0 ? 0 : Math.min(100, (total / cap) * 100);
  const boxPct = estoura && total > 0 ? (cap / total) * 100 : 100;
  const hatchPct = estoura && cap > 0 ? ((total - cap) / cap) * 100 : 0;

  return (
    <>
      {sheet && (
        <>
          <div className={`cap${estoura ? ' cap--estoura' : ''}`}>
            <span className="cap__n">{oneDecimal(total)}</span>
            <span className="cap__d">{t('gm.skills.de', { cap: oneDecimal(cap) })}</span>
            <span className="cap__tag">
              {estoura
                ? <b>{t('gm.skills.acima', { n: oneDecimal(total - cap) })}</b>
                : t('gm.skills.sobram', { n: oneDecimal(sobra) })}
              {dirty && parsed?.ok && (
                <>
                  {' · '}
                  <b className={deltaTotal < 0 ? 'sk__d--dn' : 'sk__d--up'}>{sinal(deltaTotal)}</b>
                </>
              )}
            </span>
          </div>
          <div className={`cap__bar${estoura ? ' cap__bar--estoura' : ''}`} style={estoura ? { width: `${boxPct}%` } : undefined}>
            <i style={{ width: `${barPct}%` }} />
            {estoura && <u style={{ width: `${hatchPct}%` }} />}
          </div>
        </>
      )}

      {aplicado && (
        <div className="notice notice--ok">
          <b>
            {aplicado.n > 0 && t('gm.skills.aplicado', { n: aplicado.n, delta: sinal(aplicado.delta) })}
            {aplicado.n > 0 && aplicado.atrib > 0 && ' '}
            {aplicado.atrib > 0 && t('gm.atributos.aplicado', { n: aplicado.atrib })}
          </b>
          <br />
          <span className="gm-skills__nota">{t('gm.skills.aplicadoNota', { nome: name })}</span>
        </div>
      )}

      {sheet && (
        <div className="rolagem">
          {atrib && (
            <div className="grupo grupo--atrib">
              <p className="grupo__n">
                {t('gm.atributos.titulo')}{' '}
                <span className={atribEstoura ? 'sk__d--dn' : undefined}>
                  {t('gm.atributos.soma', { n: atribOk ? atribSoma : '—', cap: atrib.statCap })}
                </span>
              </p>
              {ATRIBUTOS.map(([id, chave]) => {
                const bruto = atribDraft[id];
                const atual = atribValores?.[id] ?? null;
                const d = atual === null ? 0 : atual - atrib[id];
                return (
                  <div key={id} className={`sk${d !== 0 ? ' is-mexida' : ''}${atual === null ? ' is-erro' : ''}`}>
                    <span className="sk__n">{t(chave)}</span>
                    <span className="sk__de">{atrib[id]}</span>
                    <span className="sk__campo">
                      <input
                        className="sk__in"
                        inputMode="numeric"
                        autoComplete="off"
                        aria-label={t(chave)}
                        aria-invalid={atual === null || undefined}
                        value={bruto}
                        disabled={busy}
                        onChange={e => {
                          setAtribDraft(prev => ({ ...prev, [id]: e.target.value }));
                          setAplicado(null);
                        }}
                      />
                      <span className="sk__bar">
                        <i style={{ width: `${Math.min(100, Math.max(0, ((atual ?? 0) / atrib.teto) * 100))}%` }} />
                      </span>
                    </span>
                    <span className={`sk__d ${d > 0 ? 'sk__d--up' : d < 0 ? 'sk__d--dn' : 'sk__d--nil'}`}>
                      {d === 0 ? '-' : d > 0 ? `+${d}` : `−${-d}`}
                    </span>
                    <span className="sk__lock" />
                  </div>
                );
              })}
            </div>
          )}
          {grupos.map(g => {
            const vivos = g.skills.filter(s => (parsed?.ok ? parsed.valores[s.id] : s.value) > 0).length;
            return (
              <div className="grupo" key={g.id}>
                <p className="grupo__n">{t(GROUP_CHAVE[g.id])} <span>{vivos}/{g.skills.length}</span></p>
                {g.skills.map(skill => {
                  const bruto = draft[skill.id] ?? oneDecimal(skill.value);
                  const atual = parsed?.ok ? parsed.valores[skill.id] : parseCasa(bruto);
                  const mexida = parsed?.ok && atual !== skill.value;
                  const zero = (atual ?? skill.value) === 0;
                  const d = typeof atual === 'number' ? atual - skill.value : 0;
                  return (
                    <div
                      key={skill.id}
                      className={`sk${mexida ? ' is-mexida' : ''}${zero ? ' is-zero' : ''}`}
                    >
                      <span className="sk__n">{nomeDaSkill(skill.id, skill.name)}</span>
                      <span className="sk__de">{oneDecimal(skill.value)}</span>
                      <span className="sk__campo">
                        <input
                          className="sk__in"
                          inputMode="decimal"
                          autoComplete="off"
                          value={bruto}
                          disabled={busy}
                          onChange={e => {
                            setDraft(prev => ({ ...prev, [skill.id]: e.target.value }));
                            setAtalho(null);
                            setAplicado(null);
                          }}
                        />
                        <span className="sk__bar">
                          <i style={{ width: `${Math.min(100, Math.max(0, atual ?? 0))}%` }} />
                        </span>
                      </span>
                      <span className={`sk__d ${d > 0 ? 'sk__d--up' : d < 0 ? 'sk__d--dn' : 'sk__d--nil'}`}>
                        {d === 0 ? '-' : sinal(d)}
                      </span>
                      <span className="sk__lock" title={skill.lock}>{LOCK_ICON[skill.lock]}</span>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}

      {travadas.length === 1 && (
        <div className="notice notice--ok">
          {t('gm.skills.trava', {
            skill: nomeDaSkill(travadas[0].id, travadas[0].name),
            glifo: LOCK_ICON.locked
          })}
        </div>
      )}
      {travadas.length > 1 && (
        <div className="notice notice--ok">
          {t('gm.skills.travas', { n: travadas.length })}
        </div>
      )}

      {atrib && !atribOk && (
        <div className="notice">{t('gm.atributos.invalido', { teto: atrib.teto })}</div>
      )}
      {atribEstoura && (
        <div className="notice">{t('gm.atributos.estouro', { cap: atrib!.statCap, n: atribSoma - atrib!.statCap })}</div>
      )}

      {estoura && parsed?.ok && (
        <div className="notice">
          {t('gm.skills.estouro', { cap: oneDecimal(cap), n: oneDecimal(total - cap) })}
        </div>
      )}

      {atalhos && sheet && (
        <>
          <p className="gm-sec__t">{t('gm.skills.atalhos')}</p>
          <div className="gm-acoes gm-skills__atalhos">
            <button type="button" className="btn btn--tiny" disabled={busy} onClick={() => aplicarAtalho('zero')}>
              {t('gm.skills.zerar')}
            </button>
            <span className="gm-skills__de">{t('gm.skills.deProfissao')}</span>
            <button
              type="button"
              className="btn btn--tiny"
              aria-pressed={atalho === 'warrior'}
              disabled={busy}
              onClick={() => aplicarAtalho('warrior')}
            >
              {t('gm.skills.guerreiro')}
            </button>
            <button
              type="button"
              className="btn btn--tiny"
              aria-pressed={atalho === 'mage'}
              disabled={busy}
              onClick={() => aplicarAtalho('mage')}
            >
              {t('gm.skills.mago')}
            </button>
            <button
              type="button"
              className="btn btn--tiny"
              aria-pressed={atalho === 'smith'}
              disabled={busy}
              onClick={() => aplicarAtalho('smith')}
            >
              {t('gm.skills.ferreiro')}
            </button>
          </div>
        </>
      )}

      {gabarito && (
        <div className="notice">
          {t('gm.skills.gabarito', {
            z: gabarito.zerarN,
            dz: sinal(gabarito.zerarV),
            k: gabarito.rebaixarN,
            dk: sinal(gabarito.rebaixarV),
            total: sinal(gabarito.total)
          })}
        </div>
      )}

      {error && <div className="notice">{error}</div>}

      <div className="rodape">
        <span className="rodape__i">
          {!dirty && t('gm.skills.nadaMudou')}
          {mexidas.length > 0 && parsed?.ok && t('gm.skills.mexidas', { n: mexidas.length, delta: sinal(deltaTotal) })}
          {mexidas.length > 0 && atribMexidos.length > 0 && ' · '}
          {atribMexidos.length > 0 && t('gm.atributos.mexidos', { n: atribMexidos.length })}
        </span>
        {!atalhos && (
          <button type="button" className="btn btn--tiny btn--ghost" onClick={() => setAtalhos(true)}>
            {t('gm.skills.atalhos')}
          </button>
        )}
        <button type="button" className="btn btn--tiny btn--ghost" disabled={busy || !dirty} onClick={reverter}>
          {t('gm.skills.reverter')}
        </button>
        {estoura && (
          <button type="button" className="btn btn--tiny btn--ghost" disabled title={t('gm.skills.tetoTravado')}>
            {t('gm.skills.subirTeto')}
          </button>
        )}
        {aplicado && !dirty ? (
          <button type="button" className="btn btn--tiny" onClick={onClose}>{t('gm.skills.fechar')}</button>
        ) : (
          <button
            type="button"
            className={`btn btn--tiny ${deltaTotal < 0 ? 'btn--danger' : 'btn--primary'}`}
            disabled={busy || !dirty || !parsed?.ok || estoura || !atribOk || atribEstoura}
            onClick={() => void aplicar()}
          >
            {t('gm.skills.aplicar')}
          </button>
        )}
      </div>
    </>
  );
}
